import {
  mapQueryDataSources,
  mapQueryUserCountsWidget,
  queryDataSources,
  queryUserCountsWidget
} from './queries'
import service, { InviteUserParams, UserAccess, UserRole } from '../../services/api/apiService'
import {
  GDriveUsersWidgets,
  GDriveUsersWidgetsQueryParams,
  GMailUsersWidget,
  GMailUsersWidgetQueryParams,
  OutlookUsersWidget,
  OutlookUsersWidgetQueryParams
} from '../../services/api/apiTypes'
import { getStatusMessage } from '../../services/api/apiStatuses'
import { defaultSortParams, getSortDirection, SortParams } from '../../utils/sortUtil'
import { APIResponseMsg, IGetUsersParams } from '../../interfaces'
import { DATA_SOURCE_TYPES, IS_AT_RISK, IS_SENSITIVE } from '../../constants'
import { IRoleDetails, UserRole as UserRoleNames } from '../configureRoles/constants'
import graphqlService from '../../services/graphqlService'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export type UserDataSourceConfig = {
  id: string
  owner?: string
  coOwner?: string
  isRemoved?: boolean
}

export enum DataSourceOwnershipTypes {
  owner = 'owner',
  coOwner = 'coOwner'
}
export type UserDataSourceConfigParams = {
  id: string
  userEmail?: string
  ownership?: DataSourceOwnershipTypes
}

const datasourceOwnerRoles = [
  UserRoleNames.ADMIN,
  UserRoleNames.PLATFORM_OWNER,
  UserRoleNames.DATASOURCE_OWNER,
  UserRoleNames.DATASOURCE_VIEWER
]

export const canRoleBeDataSourceOwner = (role?: UserRoleNames) => {
  return role && datasourceOwnerRoles.includes(role)
}

export interface IUser {
  name?: string
  firstName?: string
  lastName?: string
  id?: string
  description?: string
  type?: string
  recordCount?: number
  classifiedFilesCount?: number
  unclassifiedFilesCount?: number
  piiCount?: number
  riskyCount?: number
  emailId?: string
  lastActiveAt?: string
  accessLevels?: UserAccess[]
  createdAt?: string
  createdBy?: {
    userId?: string
    userName?: string
  }
  roles?: {
    name: UserRole
    displayName: string
    description: string
    scope?: string
    accessLevels: UserAccess[]
  }[] &
    IRoleDetails[] //TODO: needs to be completey replaced with IRoleDetails[]
  roleDetail?: IRoleDetails[]
  userDataSourceConfig?: UserDataSourceConfig[]
}
export type UsersListSettings = {
  list?: IUser[]
  total?: number
  sort: SortParams
}

export interface UsersCountCombined {
  all?: number
  risky?: number
  sensitive?: number
}

export interface UsersDatasourceInfo {
  id: string
  name: string
  type?: DATA_SOURCE_TYPES
  createdBy?: string
  idpId?: string
}

export interface UserState {
  gDrive?: GDriveUsersWidgets
  all: UsersListSettings
  risky: UsersListSettings
  sensitive: UsersListSettings
  gMail?: GMailUsersWidget
  outLook?: OutlookUsersWidget
  usersCountCombined: UsersCountCombined
  inviteUserResponse?: APIResponseMsg
  roles?: UserRole[]
  rolesList?: IRoleDetails[]
  user?: IUser
  dataSourcesConfig?: UserDataSourceConfig[]
  showDatasourceModal?: boolean
  dataSources?: UsersDatasourceInfo[]
  usersListForDropdown?: IUser[]
  widgetUserCounts?: UserCountsWidget
}

const initialList = {
  sort: defaultSortParams
}

export const initialState: UserState = {
  all: { ...initialList },
  risky: { ...initialList },
  sensitive: { ...initialList },
  usersCountCombined: {},
  usersListForDropdown: []
}

export const fetchUserGDriveWidgetData = createAsyncThunk(
  'users/gDrive',
  async (filters: GDriveUsersWidgetsQueryParams) => await service.getGDriveUsersWidgetData(filters)
)
export const fetchDatasourceUsersList = createAsyncThunk(
  'datasource/users/list',
  async (filters: IGetUsersParams) => await service.getDatasourceUsersList(filters)
)

export const ACTION_USER_DATA_SOURCES_LIST = 'users/datasources/list'
export const fetchDataSources = createAsyncThunk(ACTION_USER_DATA_SOURCES_LIST, async () => {
  const results = await graphqlService.execute(queryDataSources())
  return mapQueryDataSources(results)
})

// Roles
export const ACTION_FETCH_AVAILABLE_ROLES = 'users/roles'
export const fetchAvailableUserRoles = createAsyncThunk(
  ACTION_FETCH_AVAILABLE_ROLES,
  async () => await service.getUserRolesList()
)

export const ACTION_ROLE_DELETE = 'users/deleteRole'
export const deleteRole = createAsyncThunk(
  ACTION_ROLE_DELETE,
  async (id: string) => await service.deleteRole(id)
)

// Users
export const ACTION_FETCH_USERS = 'users/list'
export const fetchUsersList = createAsyncThunk(
  ACTION_FETCH_USERS,
  async (filters?: IGetUsersParams) => {
    return await service.getUsersList(filters)
  }
)

export const ACTION_FETCH_USER = 'get-user/id'
export const fetchUserById = createAsyncThunk(
  ACTION_FETCH_USER,
  async (id: string) => await service.getUserById(id)
)

export const ACTION_USER_INVITE = 'user/invite'
export const inviteNewUser = createAsyncThunk(
  ACTION_USER_INVITE,
  async (params: InviteUserParams, { rejectWithValue }) => {
    try {
      return await service.postInviteUser(params)
    } catch (error: any) {
      const statusMessage = getStatusMessage(error, 'users.invite.error')
      return rejectWithValue({ statusMessage })
    }
  }
)

export type UserCountsWidget = {
  totalUsers?: number
  activeUsers?: number
  deletedUsers?: number
  disabledUsers?: number
  externalUsers?: number
  guestUsers?: number
}
export const ACTION_FETCH_USER_COUNTS_WIDGET = 'users/widgetUserCounts'
export const fetchUserCountsWidget = createAsyncThunk(ACTION_FETCH_USER_COUNTS_WIDGET, async () => {
  const results = await graphqlService.execute(queryUserCountsWidget())
  return mapQueryUserCountsWidget(results)
})

export const ACTION_FETCH_USER_ROLES = 'users/rolesV2'
export const fetchUserRoles = createAsyncThunk(
  ACTION_FETCH_USER_ROLES,
  async () => await service.getUserRolesV2()
)

export const ACTION_FETCH_USERS_FOR_DROPDOWN = 'users/list/forDropdown'
export const fetchUsersListForDropdown = createAsyncThunk(
  ACTION_FETCH_USERS_FOR_DROPDOWN,
  async (filters: IGetUsersParams) => {
    return await service.getUsersList(filters)
  }
)
export const mapUserToUpdateParams = (user?: IUser | null): UpdateUserParams => {
  return {
    userId: user?.id || '',
    emailId: user?.emailId || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    role: user?.roles?.[0].name || '',
    userDataSourceConfig: user?.userDataSourceConfig || []
  }
}

export type MapUserDatasourceConfigByRoleParams = {
  role: string
  email: string
  datasourceConfig?: UserDataSourceConfig[]
}
export const mapUserDatasourceConfigByRole = (
  params: MapUserDatasourceConfigByRoleParams
): UserDataSourceConfig[] => {
  const { role, email, datasourceConfig = [] } = params

  const canUserBeOwner = canRoleBeDataSourceOwner(role as UserRoleNames)

  return datasourceConfig?.map((ds) => {
    if (canUserBeOwner) return ds

    const changeOwnerTo = ds.coOwner === email ? '' : ds.coOwner
    return {
      ...ds,
      owner: ds.owner === email ? changeOwnerTo : ds.owner,
      coOwner: email
    }
  })
}

export interface UpdateUserParams {
  userId: string
  emailId?: string
  firstName?: string
  lastName?: string
  role?: string
  replacementDatasourceOwner?: string
  userDataSourceConfig?: UserDataSourceConfig[] // BE takes it into account only for DS owner/viewer roles
}
export const ACTION_USER_INFO_UPDATE = 'user/update'
export const updateUserInfo = createAsyncThunk(
  ACTION_USER_INFO_UPDATE,
  async (params: UpdateUserParams, { rejectWithValue }) => {
    try {
      return await service.patchUser(params)
    } catch (error: any) {
      const statusMessage = getStatusMessage(error, 'users.update.error')

      return rejectWithValue({ statusMessage })
    }
  }
)

export const ACTION_USER_DELETE = 'user/delete'
export const deleteUser = createAsyncThunk(
  ACTION_USER_DELETE,
  async (params: UpdateUserParams, { rejectWithValue }) => {
    try {
      if (params.userDataSourceConfig?.length) {
        await service.patchUser(params)
      }
      return await service.deleteUser(params.userId)
    } catch (error: any) {
      const statusMessage = getStatusMessage(error, 'users.delete.error')
      return rejectWithValue({ statusMessage })
    }
  }
)

export const fetchUsersCombinedCount = createAsyncThunk(
  'users/combinedCount',
  async (filters: IGetUsersParams) => {
    const allResult = await service.getUsersList(filters)
    const isAtRiskResult = await service.getDatasourceUsersList({
      ...filters,
      [IS_AT_RISK]: true
    })
    const isSensitiveResult = await service.getDatasourceUsersList({
      ...filters,
      [IS_SENSITIVE]: true
    })
    return {
      all: allResult.total,
      risky: isAtRiskResult.total,
      sensitive: isSensitiveResult.total
    }
  }
)

export const fetchUsersGMailWidgetData = createAsyncThunk(
  'users/gMail',
  async (filters: GMailUsersWidgetQueryParams) => await service.getGMailUsersWidgetData(filters)
)

export const fetchUsersOutlookWidgetData = createAsyncThunk(
  'users/outLook',
  async (filters: OutlookUsersWidgetQueryParams) => await service.getOutlookUsersWidgetData(filters)
)

export const fetchDatasources = createAsyncThunk(
  'users/datasources',
  async (filters: OutlookUsersWidgetQueryParams) => await service.getOutlookUsersWidgetData(filters)
)

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setSort: (state, { payload }) => {
      state[payload.list].sort = getSortDirection(state[payload.list].sort, payload.column)
    },
    setDataSourceConfig: (state, { payload }) => {
      state.dataSourcesConfig = payload
    },
    updateDataSourceConfigOwner: (state, { payload }: { payload: UserDataSourceConfigParams }) => {
      const { id, ownership, userEmail } = payload
      let owner
      let coOwner

      const foundDs = state.dataSources?.find((ds) => ds.id === id)
      const foundInConfig = state.dataSourcesConfig?.find((ds) => ds.id === id)

      if (ownership === DataSourceOwnershipTypes.owner) {
        owner = userEmail
        coOwner = foundDs?.createdBy === userEmail ? '' : foundDs?.createdBy
      } else {
        owner = foundDs?.createdBy
        coOwner = foundDs?.createdBy === userEmail ? '' : foundDs?.createdBy
      }

      const updatedDs = { id, owner, coOwner, isRemoved: false }

      if (foundInConfig) {
        state.dataSourcesConfig = state.dataSourcesConfig?.map((ds) =>
          ds.id === updatedDs.id ? updatedDs : ds
        )
      } else {
        state.dataSourcesConfig = [...(state.dataSourcesConfig || []), updatedDs]
      }
    },
    resetWidget: (state) => {
      state.gDrive = initialState.gDrive
      state.gMail = initialState.gMail
      state.outLook = initialState.outLook
      state.usersCountCombined = initialState.usersCountCombined
    },
    clearList: (state) => {
      state.all = { ...initialList }
      state.sensitive = { ...initialList }
      state.risky = { ...initialList }
    },
    resetInviteUser: (state) => {
      state.inviteUserResponse = initialState.inviteUserResponse
    },
    resetUser: (state) => {
      state.user = initialState.user
      state.dataSourcesConfig = initialState.dataSourcesConfig
    },
    resetAvailableUserRoles: (state) => {
      state.roles = initialState.roles
    },
    setShowDatasourceModal: (state, { payload }) => {
      state.showDatasourceModal = payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserGDriveWidgetData.fulfilled, (state, action) => {
      state.gDrive = action.payload
    })
    builder.addCase(fetchDatasourceUsersList.fulfilled, (state, { payload }) => {
      const { list, total } = payload

      if (payload.risky) {
        state.risky.list = list
        state.risky.total = total
      } else if (payload.sensitive) {
        state.sensitive.list = list
        state.sensitive.total = total
      } else {
        state.all.list = list
        state.all.total = total
      }
    })
    builder.addCase(fetchDataSources.fulfilled, (state, { payload }) => {
      state.dataSources = payload
    })
    builder.addCase(fetchUsersList.fulfilled, (state, { payload }) => {
      const { list, total } = payload
      state.all.list = list
      state.all.total = total
    })
    builder.addCase(fetchUsersList.rejected, (state) => {
      state.all.list = []
      state.all.total = 0
    })
    builder.addCase(fetchUsersListForDropdown.fulfilled, (state, { payload }) => {
      const { list } = payload
      state.usersListForDropdown = list
    })
    builder.addCase(fetchUsersCombinedCount.fulfilled, (state, action) => {
      state.usersCountCombined.all = action.payload.all
      state.usersCountCombined.risky = action.payload.risky
      state.usersCountCombined.sensitive = action.payload.sensitive
    })
    builder.addCase(fetchUserCountsWidget.fulfilled, (state, { payload }) => {
      state.widgetUserCounts = payload
    })
    builder.addCase(fetchUsersGMailWidgetData.fulfilled, (state, action) => {
      state.gMail = action.payload
    })
    builder.addCase(fetchUsersOutlookWidgetData.fulfilled, (state, action) => {
      state.outLook = action.payload
    })
    builder.addCase(inviteNewUser.rejected, (state, action) => {
      state.inviteUserResponse = { error: action.error.message || '', success: '' }
    })
    builder.addCase(fetchAvailableUserRoles.fulfilled, (state, { payload }) => {
      state.roles = payload
    })
    builder.addCase(fetchUserById.fulfilled, (state, { payload }) => {
      const { user } = payload
      state.user = user
    })
    builder.addCase(fetchUserRoles.fulfilled, (state, { payload }) => {
      state.rolesList = payload
    })
  }
})

export const {
  setSort,
  setDataSourceConfig,
  updateDataSourceConfigOwner,
  clearList,
  resetWidget,
  resetInviteUser,
  resetUser,
  resetAvailableUserRoles,
  setShowDatasourceModal
} = usersSlice.actions

export default usersSlice.reducer
